// Message Thread Component - Displays conversation messages
import React, { useEffect, useRef } from 'react';
import { User, Check, CheckCheck, Clock } from 'lucide-react';
import { DirectMessage, TypingIndicator } from '../../types/messaging';

interface MessageThreadProps {
  messages: DirectMessage[];
  currentUserId: string;
  currentUserRole: 'provider' | 'client';
  otherParticipantName: string;
  typingIndicator: TypingIndicator | null;
  onMessagesViewed?: () => void;
}

const MessageThread: React.FC<MessageThreadProps> = ({
  messages,
  currentUserId,
  currentUserRole,
  otherParticipantName,
  typingIndicator,
  onMessagesViewed
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typingIndicator]);

  // Mark messages as viewed when messages arrive (skip for empty conversations)
  useEffect(() => {
    if (onMessagesViewed && messages.length > 0) {
      onMessagesViewed();
    }
  }, [messages, onMessagesViewed]);

  // Format timestamp for display
  const formatMessageTime = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format date for date separator
  const formatDateSeparator = (date: Date): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString([], {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  // Check if we need a date separator
  const needsDateSeparator = (
    currentMessage: DirectMessage,
    previousMessage: DirectMessage | null
  ): boolean => {
    if (!previousMessage) return true;
    return (
      currentMessage.timestamp.toDateString() !==
      previousMessage.timestamp.toDateString()
    );
  };

  // Check if message is from current user
  const isOwnMessage = (message: DirectMessage): boolean => {
    return message.senderId === currentUserId;
  };

  // Render read status icon
  const renderReadStatus = (message: DirectMessage) => {
    if (!isOwnMessage(message)) return null;

    if (message.read) {
      return (
        <CheckCheck
          size={14}
          color="#3b82f6"
          aria-label="Read"
          style={{ marginLeft: '4px' }}
        />
      );
    } else {
      return (
        <Check
          size={14}
          color="#64748b"
          aria-label="Delivered"
          style={{ marginLeft: '4px' }}
        />
      );
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        backgroundColor: '#0f172a'
      }}
      role="log"
      aria-label="Message history"
      aria-live="polite"
    >
      {messages.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#64748b',
            textAlign: 'center',
            padding: '40px'
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px'
            }}
          >
            <User size={32} color="#64748b" />
          </div>
          <p style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#94a3b8' }}>
            Start a conversation with {otherParticipantName}
          </p>
          <p style={{ margin: 0, fontSize: '13px' }}>
            Messages are secure and HIPAA-compliant
          </p>
        </div>
      ) : (
        <>
          {messages.map((message, index) => {
            const previousMessage = index > 0 ? messages[index - 1] : null;
            const showDateSeparator = needsDateSeparator(message, previousMessage);
            const isOwn = isOwnMessage(message);

            return (
              <React.Fragment key={message.id}>
                {/* Date Separator */}
                {showDateSeparator && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '20px 0',
                      position: 'relative'
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        height: '1px',
                        backgroundColor: '#334155'
                      }}
                    />
                    <span
                      style={{
                        backgroundColor: '#0f172a',
                        padding: '4px 12px',
                        fontSize: '12px',
                        color: '#64748b',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      {formatDateSeparator(message.timestamp)}
                    </span>
                  </div>
                )}

                {/* Message Bubble */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                    marginBottom: '8px'
                  }}
                >
                  <div
                    style={{
                      maxWidth: '70%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isOwn ? 'flex-end' : 'flex-start'
                    }}
                  >
                    {/* Sender Name (for received messages) */}
                    {!isOwn && (
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#94a3b8',
                          marginBottom: '4px',
                          marginLeft: '12px'
                        }}
                      >
                        {message.senderName}
                      </span>
                    )}

                    {/* Message Content */}
                    <div
                      style={{
                        backgroundColor: isOwn ? '#3b82f6' : '#1e293b',
                        color: '#f8fafc',
                        padding: '10px 14px',
                        borderRadius: isOwn
                          ? '16px 16px 4px 16px'
                          : '16px 16px 16px 4px',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        wordBreak: 'break-word'
                      }}
                    >
                      {message.isDeleted ? (
                        <span style={{ fontStyle: 'italic', opacity: 0.7 }}>
                          {message.content}
                        </span>
                      ) : (
                        message.content
                      )}
                    </div>

                    {/* Timestamp and Read Status */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '4px',
                        fontSize: '11px',
                        color: '#64748b'
                      }}
                    >
                      <span>{formatMessageTime(message.timestamp)}</span>
                      {message.isEdited && (
                        <span style={{ marginLeft: '4px' }}>(edited)</span>
                      )}
                      {renderReadStatus(message)}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* Typing Indicator */}
          {typingIndicator && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginBottom: '8px'
              }}
            >
              <div
                style={{
                  backgroundColor: '#1e293b',
                  padding: '12px 16px',
                  borderRadius: '16px 16px 16px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <span
                  style={{
                    fontSize: '13px',
                    color: '#94a3b8',
                    marginRight: '8px'
                  }}
                >
                  {typingIndicator.userName} is typing
                </span>
                <div style={{ display: 'flex', gap: '3px' }}>
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: '#64748b',
                        animation: `typingBounce 1.4s ease-in-out ${i * 0.2}s infinite`
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </>
      )}

      {/* CSS Animation for typing indicator */}
      <style>
        {`
          @keyframes typingBounce {
            0%, 60%, 100% {
              transform: translateY(0);
            }
            30% {
              transform: translateY(-4px);
            }
          }
        `}
      </style>
    </div>
  );
};

export default MessageThread;
